import axios from "axios"
import { RatgeberArticlePage, RatgeberCategoryPage } from "../../../src/ratgeber/_types"
import styles from "./category.module.scss"
import StyledText from "../../../src/typography/typography-component"
import DOMPurify from "isomorphic-dompurify"
import SkinuvitaSeoHead from "../../../src/components/LandingPage/SkinuvitaSeoHead"
import { breadcrumbSchema } from "../../../src/seo/jsonLd"
import { buildCanonicalUrl } from "../../../src/seo/siteConfig"
import BehindNavBar from "../../../src/components/BehindNavBar/BehindNavBar"
import Breadcrumb from "../../../src/ratgeber/breadcrumb"
import CategoryArticleCard from "../../../src/components/ArticleCard/CategoryArticleCard"
import Footer from "../../../src/components/footer"
import NavBarNotOnHomepage from "../../../src/components/LandingPage/navbars/NavBarNotOnHomepage"
import { GetStaticPaths, GetStaticProps } from "next"
import { fetchAndCacheImage } from "../../../src/utils/fetchAndCacheImages"
import { normalizeSlug } from "../../../src/utils/blogBuildingUtils"
import { useRouter } from "next/router"
import { Fragment } from "react"
import Divider from "@mui/material/Divider"
import skColors from "../../../styles/skColors"
import CategoryButtons from "../../../src/ratgeber/components/CategoryButtons/CategoryButtons"

export const getStaticPaths: GetStaticPaths = async () => {
  try {
    const categoryResponse = await axios.get(
      `${process.env.NEXT_PUBLIC_CONTENT_URL}/api/v2/pages/?type=ratgeber.RatgeberCategoryPage`,
    )

    const categories = categoryResponse.data.items

    const paths = categories.map((cat: any) => ({
      params: { category: cat.meta.slug },
    }))

    return {
      paths,
      fallback: "blocking",
    }
  } catch (error) {
    console.error("Failed to fetch category paths:", error)
    return {
      paths: [],
      fallback: "blocking",
    }
  }
}


// Fetch all pages from paginated API endpoint
const fetchAllPages = async (url: string) => {
  const allItems: any[] = []
  let offset = 0
  const limit = 20
  let hasMore = true

  while (hasMore) {
    try {
      // Try with offset/limit first
      let fetchUrl = url
      if (url.includes("?")) {
        fetchUrl = `${url}&offset=${offset}&limit=${limit}`
      } else {
        fetchUrl = `${url}?offset=${offset}&limit=${limit}`
      }

      const res = await axios.get(fetchUrl)
      const items = res.data.items || []

      if (items.length === 0) {
        hasMore = false
      } else {
        allItems.push(...items)
        offset += items.length
        hasMore = items.length === limit
      }
    } catch (error: any) {
      console.warn(`Failed to fetch at offset ${offset}:`, error?.response?.status || error?.message)
      hasMore = false
    }
  }

  return allItems
}


export const getStaticProps: GetStaticProps = async ({ params, locale: contextLocale }) => {
  const categorySlug = params?.category as string
  const normalizedCategorySlug = normalizeSlug(categorySlug)
  const locale = (contextLocale as string) || "de"

  let messages
  try {
    messages = (await import(`../../../src/intl/${locale}.json`)).default
  } catch (err) {
    // fallback to default
    messages = (await import(`../../../src/intl/de.json`)).default
  }

  try {
    const [categoryListRes, articleListRes] = await Promise.all([
      axios.get(`${process.env.NEXT_PUBLIC_CONTENT_URL}/api/v2/pages/?type=ratgeber.RatgeberCategoryPage&fields=*`),
      fetchAllPages(`${process.env.NEXT_PUBLIC_CONTENT_URL}/api/v2/pages/?type=ratgeber.RatgeberArticlePage&fields=*`),
    ])

    const categories = categoryListRes.data.items || []
    const categoryPage = categories.find(
      (c: any) => normalizeSlug(c?.meta?.slug || "") === normalizedCategorySlug,
    )
    if (!categoryPage) return { notFound: true }

    let categoryDetailRes: { data: RatgeberCategoryPage }
    try {
      categoryDetailRes = await axios.get(
        `${process.env.NEXT_PUBLIC_CONTENT_URL}/api/v2/pages/${categoryPage.id}/?fields=*`,
      )
    } catch (detailError) {
      console.warn(`Failed to fetch category detail for ${categorySlug}, falling back to article list`, detailError)
      categoryDetailRes = null
    }

    const allArticles = (articleListRes || []) as RatgeberArticlePage[]

    let articleSourceChildren = categoryDetailRes?.data?.children || []
    if (articleSourceChildren.length === 0) {
      console.warn(
        `Category ${categorySlug} has no children in detail response, falling back to article list filtering`,
      )
      articleSourceChildren = (allArticles as any)
        .filter((article) => normalizeSlug(article?.category_page?.slug || "") === normalizedCategorySlug)
    }

    const relatedArticles = (articleSourceChildren as any[]).slice(0, 100)

    const children = await Promise.all(
      relatedArticles.map(async (child: any) => {
        let localImage: string | null = null
        const bannerImage = child.banner_image
        const slug = child.meta?.slug || child.slug

        if (bannerImage && typeof bannerImage === "object" && bannerImage.full_url) {
          const filename = `${slug}-${bannerImage.id}.webp`
          try {
            localImage = await fetchAndCacheImage(bannerImage.full_url, filename)
          } catch (imageError) {
            console.warn(`Failed to cache category child image for ${slug}`, imageError)
          }
        }
        return {
          id: child.id,
          title: child.title,
          slug: slug,
          description: child.description,
          banner_image:
            localImage ||
            (typeof bannerImage === "object" ? bannerImage?.full_url : null) ||
            (typeof bannerImage === "string" ? bannerImage : null) ||
            `${process.env.NEXT_PUBLIC_CONTENT_URL}${typeof bannerImage === "object" ? bannerImage?.url : ""}`,
        }
      }),
    )

    return {
      props: {
        categoryPage: {
          ...(categoryDetailRes?.data || categoryPage),
          children,
        },
        categories,
        locale: locale,
        messages: messages,
      },
      revalidate: 60,
    }
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      console.warn(`Category page not found for slug: ${categorySlug}`)
      return { notFound: true, revalidate: 60 }
    }

    console.error(`Failed to build category page for slug: ${categorySlug}`, error)
    return { notFound: true, revalidate: 60 }
  }
}

type Props = {
  categoryPage: RatgeberCategoryPage
  categories: RatgeberCategoryPage[]
}


const RatgeberCategory = ({ categoryPage, categories }: Props) => {
  const sanitizedHtml = DOMPurify.sanitize(categoryPage.intro)
  // The intro is rich text; the meta description needs plain text within ~160 chars.
  const categoryIntroText = sanitizedHtml
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 158)
  const router = useRouter()
  return (
    <>
      <SkinuvitaSeoHead
        title={`${categoryPage.title} | Skinuvita Ratgeber`}
        description={
          categoryIntroText ||
          `Artikel aus der Kategorie ${categoryPage.title} im Skinuvita Ratgeber rund um Lichttherapie, Schuppenflechte und Neurodermitis.`
        }
        canonicalPath={`/ratgeber/${categoryPage.meta.slug}`}
        jsonLd={[
          breadcrumbSchema([
            { name: "Startseite", url: buildCanonicalUrl("/") },
            { name: "Ratgeber", url: buildCanonicalUrl("/ratgeber") },
            { name: categoryPage.title, url: buildCanonicalUrl(`/ratgeber/${categoryPage.meta.slug}`) },
          ]),
        ]}
      />
      <NavBarNotOnHomepage />
      <div className={styles.root}>
        <div>
          <BehindNavBar />
          <div className={styles.ratgeberCategoryPage}>
            <Breadcrumb page={categoryPage} />
            <StyledText variant={"h1"}>{categoryPage.title}</StyledText>
            <StyledText as={"div"} variant={"pLarge"}>
              <div dangerouslySetInnerHTML={{ __html: sanitizedHtml }}></div>
            </StyledText>
            {/**/}
            <CategoryButtons categories={categories} />
            {/**/}
            <div className={styles.categoryArticleGrid}>
              {categoryPage?.children.length > 0 ? (
                categoryPage.children.map((article) => {
                  return (
                    <CategoryArticleCard
                      key={`${article.title}-${article.id}`}
                      category={categoryPage}
                      article={article}
                    />
                  )
                })
              ) : (
                <StyledText
                  variant={"pLarge"}
                >{`In dieser Kategorie scheint es noch keine Artikel zu geben!`}</StyledText>
              )}
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </>
  )
}

export default RatgeberCategory

// export async function getStaticPaths() {
//   // API-Aufruf, um alle Artikel zu laden
//   const res = await axios.get(`${process.env.NEXT_PUBLIC_CONTENT_URL}/api/v2/pages/?type=ratgeber.RatgeberCategoryPage`)
//   const posts = res.data.items
//
//   // Generiere dynamische Routen basierend auf `category` und `id`
//   const paths = posts.map((post: RatgeberCategoryPage) => ({
//     params: {
//       category: post.meta.slug, // Kategorie-Slug
//     },
//   }))
//
//   return {
//     paths,
//     fallback: false, // Andere Routen führen zu 404
//   }
// }
//
// // This function gets called at build time
// export async function getStaticProps({ params }: { params: { category: string } }) {
//   try {
//     // API-Aufruf, um die spezifischen Artikeldaten zu laden
//     const pages = await axios.get(
//       `${process.env.NEXT_PUBLIC_CONTENT_URL}/api/v2/pages/?type=ratgeber.RatgeberCategoryPage`
//     )
//     const categoryPageId = pages.data.items.find((page: RatgeberCategoryPage) => page.meta.slug === params.category).id
//     const pageResponse = await axios.get(`${process.env.NEXT_PUBLIC_CONTENT_URL}/api/v2/pages/${categoryPageId}/`)
//
//     const data = pageResponse.data
//
//     // data.children = data.children.sort(
//     //   (a, b) => new Date(b.meta.first_published_at).getTime() - new Date(a.meta.first_published_at).getTime()
//     // )
//     return {
//       revalidate: 60 * 10, // 10 min
//       props: {
//         categoryPage: pageResponse.data, // Artikeldaten
//       },
//     }
//   } catch (error) {
//     console.error("Fehler beim Abrufen der Daten:", error)
//     return {
//       notFound: true, // Zeige 404-Seite, wenn ein Fehler auftritt
//     }
//   }
// }
